import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { ELEARNIO_ACTIVITIES } from '@core/constants/elearnio-activities.constant';
import { decodeHtmlSpecialChars } from '@helper/encode-decode-helper';
import { CourseActivityService } from '@services/course-activity.service';
import { CourseService } from '@services/course.service';
import { CourseActivity } from '@models/course/course-activity.model';
import { CourseActivitySection } from '@models/course/course-activity-section.model';
import { CourseMemberAnswer } from '@models/course/member/course-member-answer.model';
import { TrainingTranslationFields } from '@models/training-translation.model';

import { LabelOverflowDirective } from './label-overflow.directive';

const ITEM_KINDS = {
  LABEL: 'label',
  MODULE: 'module',
} as const;

const MODULE_STATUSES = {
  ACTIVE: 'active',
  COMPLETED_PASS: 'completed-pass',
  COMPLETED_FAIL: 'completed-fail',
  LOCKED: 'locked',
  NOT_STARTED: 'not-started',
} as const;
type ModuleStatus = (typeof MODULE_STATUSES)[keyof typeof MODULE_STATUSES];

interface StatusDisplay {
  icon: string;
  cssClass: string;
}

type IconStatus = Exclude<
  ModuleStatus,
  typeof MODULE_STATUSES.ACTIVE | typeof MODULE_STATUSES.NOT_STARTED
>;

const STATUS_DISPLAY: Record<IconStatus, StatusDisplay> = {
  [MODULE_STATUSES.COMPLETED_PASS]: {
    icon: 'check_circle',
    cssClass: 'status-pass',
  },
  [MODULE_STATUSES.COMPLETED_FAIL]: {
    icon: 'cancel',
    cssClass: 'status-fail',
  },
  [MODULE_STATUSES.LOCKED]: {
    icon: 'lock',
    cssClass: 'status-locked',
  },
};

const MODULE_TYPE_KEY_PREFIX = 'h5p.libraries.';
const PANEL_BODY_ID = 'course-navigation-panel-body';

interface ModuleItemViewModel {
  kind: typeof ITEM_KINDS.MODULE;
  id: string;
  title: string;
  typeKey: string;
  locked: boolean;
  active: boolean;
  status: ModuleStatus;
  statusIcon: string;
  statusClass: string;
  isFirstItem: boolean;
  isLastItem: boolean;
}

interface LabelItemViewModel {
  kind: typeof ITEM_KINDS.LABEL;
  id: string;
  text: string;
  textExpanded: WritableSignal<boolean>;
  overflowing: WritableSignal<boolean>;
  isFirstItem: boolean;
  isLastItem: boolean;
}

type ItemViewModel = ModuleItemViewModel | LabelItemViewModel;

interface SectionViewModel {
  id: string;
  title: string;
  items: ItemViewModel[];
}

@Component({
  selector: 'app-course-navigation-panel',
  templateUrl: './course-navigation-panel.component.html',
  styleUrls: ['./course-navigation-panel.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    LabelOverflowDirective,
  ],
})
export class CourseNavigationPanelComponent {
  private readonly courseService = inject(CourseService);
  private readonly courseActivityService = inject(CourseActivityService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly collapsed = model(false);
  readonly sections = input<CourseActivitySection[]>([]);
  // Language the learner picked on the course overview. Empty (or the source
  // language) leaves every title at its source value.
  readonly userLanguage = input<string>('');
  // Section titles and standalone label texts for that language; module titles
  // come from the runner translation maps via `getTranslatedTitle`.
  readonly translationFields = input<TrainingTranslationFields | null>(null);
  readonly currentActivityId = input<string | null>(null);
  readonly answers = input<CourseMemberAnswer[]>([]);
  readonly freeOrder = input<boolean>(false);
  readonly isCompletedCourse = input<boolean>(false);
  readonly navigateToActivity = output<string>();
  readonly panelBodyId = PANEL_BODY_ID;
  readonly ITEM_KINDS = ITEM_KINDS;
  readonly MODULE_STATUSES = MODULE_STATUSES;
  readonly hasOverflow = signal(false);

  private readonly labelTextExpandedState = new Map<
    string,
    WritableSignal<boolean>
  >();
  private readonly labelOverflowingState = new Map<
    string,
    WritableSignal<boolean>
  >();

  constructor() {
    let previousCollapsed = this.collapsed();
    effect(() => {
      const isCollapsed = this.collapsed();
      if (isCollapsed === previousCollapsed) {
        return;
      }
      previousCollapsed = isCollapsed;
      queueMicrotask(() => this.scrollActiveModuleIntoView());
    });

    const destroyRef = inject(DestroyRef);
    effect(() => {
      this.viewModel(); // dependency: re-measure when content changes
      queueMicrotask(() => this.measureOverflow());
    });
    afterNextRender(() => this.attachOverflowObserver(destroyRef));
  }

  private scrollContainer: HTMLElement | null = null;

  private attachOverflowObserver(destroyRef: DestroyRef): void {
    this.scrollContainer = this.elementRef.nativeElement
      .firstElementChild as HTMLElement | null;
    if (!this.scrollContainer) {
      return;
    }
    const observer = new ResizeObserver(() => this.measureOverflow());
    observer.observe(this.scrollContainer);
    destroyRef.onDestroy(() => observer.disconnect());
    this.measureOverflow();
  }

  private measureOverflow(): void {
    const el = this.scrollContainer;
    if (!el) {
      return;
    }
    this.hasOverflow.set(el.scrollHeight > el.clientHeight);
  }

  private scrollActiveModuleIntoView(): void {
    const id = this.currentActivityId();
    if (id === null) {
      return;
    }
    const row = this.elementRef.nativeElement.querySelector<HTMLElement>(
      `.module-row[data-activity-id="${id}"]`,
    );
    row?.scrollIntoView({ block: 'start', behavior: 'instant' });
  }

  readonly viewModel = computed<SectionViewModel[]>(() => {
    const sortedSections = this.courseService.sortByOrder(
      this.sections() ?? [],
    );
    const unlockedIds = this.computeUnlockedIds(sortedSections);
    const currentId = this.currentActivityId();
    const answers = this.answers();
    return sortedSections.map(section => {
      const sortedActivities = this.courseService.sortByOrder(
        section.courseActivities ?? [],
      );
      const lastIdx = sortedActivities.length - 1;
      return {
        id: section.id,
        title: decodeHtmlSpecialChars(this.getSectionTitle(section)),
        items: sortedActivities.map((a, idx) =>
          this.buildItem(
            a,
            unlockedIds,
            currentId,
            answers,
            idx === 0,
            idx === lastIdx,
          ),
        ),
      };
    });
  });

  toggle(): void {
    this.collapsed.update(v => !v);
  }

  toggleLabelText(id: string): void {
    this.labelTextExpandedState.get(id)?.update(v => !v);
  }

  setLabelOverflowing(id: string, overflowing: boolean): void {
    const state = this.labelOverflowingState.get(id);
    if (state && state() !== overflowing) {
      state.set(overflowing);
    }
  }

  onLabelActivate(item: LabelItemViewModel): void {
    if (!item.overflowing()) {
      return;
    }
    this.toggleLabelText(item.id);
  }

  onLabelKeydown(item: LabelItemViewModel, event: Event): void {
    if (!item.overflowing()) {
      return;
    }
    event.preventDefault();
    this.toggleLabelText(item.id);
  }

  onModuleActivate(item: ModuleItemViewModel): void {
    if (item.locked || item.active) {
      return;
    }
    this.navigateToActivity.emit(item.id);
  }

  onModuleKeydown(item: ModuleItemViewModel, event: Event): void {
    if (item.locked || item.active) {
      return;
    }
    event.preventDefault();
    this.navigateToActivity.emit(item.id);
  }

  private buildItem(
    activity: CourseActivity,
    unlockedIds: Set<string>,
    currentId: string | null,
    answers: CourseMemberAnswer[],
    isFirstItem: boolean,
    isLastItem: boolean,
  ): ItemViewModel {
    if (this.isLabel(activity)) {
      return this.buildLabelItem(activity, isFirstItem, isLastItem);
    }
    return this.buildModuleItem(
      activity,
      unlockedIds,
      currentId,
      answers,
      isFirstItem,
      isLastItem,
    );
  }

  private computeUnlockedIds(
    sortedSections: CourseActivitySection[],
  ): Set<string> {
    const unlocked = new Set<string>();
    if (this.freeOrder() || this.isCompletedCourse()) {
      for (const section of sortedSections) {
        for (const activity of section.courseActivities ?? []) {
          unlocked.add(activity.id);
        }
      }
      return unlocked;
    }

    const answers = this.answers();
    let firstUncompletedFound = false;
    for (const section of sortedSections) {
      const activities = this.courseService.sortByOrder(
        section.courseActivities ?? [],
      );
      for (const activity of activities) {
        if (this.isLabel(activity)) {
          continue;
        }
        const answer = this.courseActivityService.getAnswer(activity, answers);
        const completed =
          this.courseActivityService.isCompletedActivityFromAnswer(answer);
        if (completed) {
          unlocked.add(activity.id);
        } else if (!firstUncompletedFound) {
          unlocked.add(activity.id);
          firstUncompletedFound = true;
        }
      }
    }
    return unlocked;
  }

  private buildLabelItem(
    activity: CourseActivity,
    isFirstItem: boolean,
    isLastItem: boolean,
  ): LabelItemViewModel {
    return {
      kind: ITEM_KINDS.LABEL,
      id: activity.id,
      text: this.getLabelText(activity),
      textExpanded: this.getOrCreateSignal(
        this.labelTextExpandedState,
        activity.id,
      ),
      overflowing: this.getOrCreateSignal(
        this.labelOverflowingState,
        activity.id,
      ),
      isFirstItem,
      isLastItem,
    };
  }

  private buildModuleItem(
    activity: CourseActivity,
    unlockedIds: Set<string>,
    currentId: string | null,
    answers: CourseMemberAnswer[],
    isFirstItem: boolean,
    isLastItem: boolean,
  ): ModuleItemViewModel {
    const locked = !unlockedIds.has(activity.id);
    const active = currentId !== null && activity.id === currentId;
    const status = this.resolveStatus(activity, active, locked, answers);
    const display = this.isIconStatus(status) ? STATUS_DISPLAY[status] : null;
    return {
      kind: ITEM_KINDS.MODULE,
      id: activity.id,
      title: decodeHtmlSpecialChars(this.getModuleTitle(activity)),
      typeKey: this.getTypeKey(activity),
      locked,
      active,
      status,
      statusIcon: display?.icon ?? '',
      statusClass: display?.cssClass ?? '',
      isFirstItem,
      isLastItem,
    };
  }

  private isIconStatus(status: ModuleStatus): status is IconStatus {
    return (
      status !== MODULE_STATUSES.ACTIVE &&
      status !== MODULE_STATUSES.NOT_STARTED
    );
  }

  private resolveStatus(
    activity: CourseActivity,
    active: boolean,
    locked: boolean,
    answers: CourseMemberAnswer[],
  ): ModuleStatus {
    if (active) {
      return MODULE_STATUSES.ACTIVE;
    }
    if (locked) {
      return MODULE_STATUSES.LOCKED;
    }
    const answer = this.courseActivityService.getAnswer(activity, answers);
    if (!this.courseActivityService.isCompletedActivityFromAnswer(answer)) {
      return MODULE_STATUSES.NOT_STARTED;
    }
    const gradeable =
      this.courseActivityService.isNonNeutralActivityFromAnswer(answer);
    const passed =
      this.courseActivityService.isSuccessfulActivityFromAnswer(answer);
    if (gradeable && !passed) {
      return MODULE_STATUSES.COMPLETED_FAIL;
    }
    return MODULE_STATUSES.COMPLETED_PASS;
  }

  private getOrCreateSignal(
    map: Map<string, WritableSignal<boolean>>,
    id: string,
  ): WritableSignal<boolean> {
    let s = map.get(id);
    if (!s) {
      s = signal(false);
      map.set(id, s);
    }
    return s;
  }

  private isLabel(activity: CourseActivity): boolean {
    return activity?.h5pLibrary?.toLowerCase() === ELEARNIO_ACTIVITIES.LABEL;
  }

  // Section/label text lives on the translation record, module titles in the
  // runner maps; all fall back to source per field.
  private getSectionTitle(section: CourseActivitySection): string {
    return (
      this.translationFields()?.sectionTitles?.[section?.id] ||
      section?.title ||
      ''
    );
  }

  private getModuleTitle(activity: CourseActivity): string {
    const language = this.userLanguage();
    const source = activity.title ?? '';
    if (!language) {
      return source;
    }
    return activity.getTranslatedTitle(language) || source;
  }

  private getLabelText(activity: CourseActivity): string {
    const translated = this.translationFields()?.moduleLabels?.[activity?.id];
    if (translated) {
      return decodeHtmlSpecialChars(translated);
    }
    try {
      const labelText =
        JSON.parse(activity?.contentJSON ?? '')?.labelText ?? '';
      return decodeHtmlSpecialChars(labelText);
    } catch {
      return '';
    }
  }

  private getTypeKey(activity: CourseActivity): string {
    const libraryId = (activity?.h5pLibrary ?? '').split(' ')[0];
    return libraryId ? `${MODULE_TYPE_KEY_PREFIX}${libraryId}` : '';
  }
}
